import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Button, DatePicker, Dropdown, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, MoreOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import qs from 'qs';
import { isMobile } from 'react-device-detect';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import BaseSelect from '@/components/Elements/BaseSelect';
import { cleanObject } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';
import CommissionDashboardDetailDrawer from './CommissionDashboardDetailDrawer';
import {
    decideCommissionItem,
    fetchCommissionApprovals,
    fetchCommissionReconciliation,
    fetchCommissionWorkflowFilters,
    processCommissionWorkflowBatch,
    reconcileCommissionItem,
} from '@/redux/actions/commissionActions';
import { lifecycleStatus, money } from './commissionLifecycleHelpers';
import { formatCommissionRate } from './commissionDashboardHelpers';
import { formatCommissionErrorItem } from '@/utils/commission/errorPresenter';
import { mapUserOptionLabels } from '@/utils/userOptionHelpers';
import { CommissionCustomerColumn, CommissionServiceColumn } from './CommissionCustomerServiceColumns';
import CommissionRelatedEmployees from './CommissionRelatedEmployees';

const { Text } = Typography;

export default function CommissionWorkflowList({ mode, embedded = false }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const isApproval = mode === 'approval';
    const allowAction = checkPermission(isApproval ? 'commission_approval' : 'commission_reconciliation', 'update');
    const query = useMemo(() => qs.parse(location.search.slice(1)), [location.search]);
    const periodKey = query.period_key || moment().format('YYYY-MM');
    const result = useSelector(state => (
        isApproval ? state.commissionReducer.approvals : state.commissionReducer.reconciliation
    )) || { data: [] };
    const users = useSelector(state => state.config.users || []);
    const [loading, setLoading] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [rejectItem, setRejectItem] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ employees: [], service_groups: [], services: [] });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});
    const [batchLoading, setBatchLoading] = useState(false);
    const [rejectForm] = Form.useForm();
    const employeeOptions = useMemo(
        () => mapUserOptionLabels(filterOptions.employees || [], users),
        [filterOptions.employees, users]
    );

    const load = useCallback(async filters => {
        setLoading(true);
        try {
            const action = isApproval ? fetchCommissionApprovals : fetchCommissionReconciliation;
            await dispatch(action({
                period_key: filters.period_key || moment().format('YYYY-MM'),
                status: filters.status || undefined,
                employee_id: filters.employee_id || undefined,
                service_group_id: filters.service_group_id || undefined,
                service_id: filters.service_id || undefined,
                page: filters.page || 1,
                per_page: filters.per_page || 30,
            }));
        } catch (error) {
            // Action đã hiển thị lỗi.
        } finally { setLoading(false); }
    }, [dispatch, isApproval]);

    useEffect(() => { load(query); }, [load, query]);
    useEffect(() => {
        dispatch(fetchCommissionWorkflowFilters(isApproval))
            .then(setFilterOptions)
            .catch(() => { });
    }, [dispatch, isApproval]);

    const changeFilter = (name, value) => {
        setSelectedRowKeys([]);
        setSelectedItems({});
        const nextQuery = cleanObject({ ...query, [name]: value, page: 1 });
        history.push({ pathname: location.pathname, search: qs.stringify(nextQuery) });
    };

    const changeTable = tablePagination => {
        const currentPageSize = Number(query.per_page) || 30;
        const nextPageSize = tablePagination.pageSize || currentPageSize;
        const nextQuery = cleanObject({
            ...query,
            page: nextPageSize !== currentPageSize ? 1 : tablePagination.current,
            per_page: nextPageSize,
        });
        history.push({ pathname: location.pathname, search: qs.stringify(nextQuery) });
    };

    const reconcile = async item => {
        try { await dispatch(reconcileCommissionItem(item.id)); load(query); }
        catch (error) { /* Action đã hiển thị lỗi. */ }
    };
    const decide = async (item, decision, note) => {
        try { await dispatch(decideCommissionItem(item.id, decision, note)); setRejectItem(null); rejectForm.resetFields(); load(query); }
        catch (error) { /* Action đã hiển thị lỗi. */ }
    };

    const isEligible = item => !!item && allowAction && item.period_status !== 'LOCKED' && (
        isApproval
            ? item.reconciliation?.status === 'RECONCILED' && item.approval?.status === 'PENDING'
            : item.reconciliation?.status === 'PENDING' && item.approval?.status === 'PENDING'
    );
    const eligibleSelectedCount = selectedRowKeys.filter(key => isEligible(selectedItems[key])).length;

    const changeSelection = keys => {
        setSelectedRowKeys(keys);
        setSelectedItems(previous => {
            const next = {};
            keys.forEach(key => {
                const current = (result.data || []).find(item => item.id === key);
                if (current || previous[key]) next[key] = current || previous[key];
            });
            return next;
        });
    };

    const showBatchResult = batchResult => {
        const failures = (batchResult.results || []).filter(item => !item.success);
        const failureLabel = resultItem => {
            const item = selectedItems[resultItem.item_id];
            return item
                ? `${item.employee?.full_name || 'Nhân viên chưa xác định'} — ${item.service?.name || 'Dịch vụ chưa xác định'}`
                : 'Một khoản đã chọn';
        };
        Modal.info({
            title: isApproval ? 'Kết quả duyệt hàng loạt' : 'Kết quả xác nhận hàng loạt',
            width: isMobile ? '100%' : '58%',
            maskClosable: false,
            okText: 'Đóng',
            content: <div>
                <p>Thành công: <strong>{batchResult.success_count || 0}</strong> / {batchResult.requested_count || 0} khoản.</p>
                {!!failures.length && <div>
                    <Text strong type="danger">Không xử lý được {failures.length} khoản:</Text>
                    <ul>{failures.map(item => <li key={item.item_id}>
                        <div><strong>{failureLabel(item)}</strong></div>
                        <div>Bước: {item.step || 'WORKFLOW'} · Mã lỗi: {item.code || 'WORKFLOW_CONFLICT'} · Item: #{item.item_id}</div>
                        <div>{formatCommissionErrorItem(item)}</div>
                        {!!item.details?.checks?.length && <ul>{item.details.checks.map(check => (
                            <li key={`${item.item_id}-${check.code}`}>{formatCommissionErrorItem(check)}</li>
                        ))}</ul>}
                        {item.action && <div>Hướng xử lý: {item.action}</div>}
                    </li>)}</ul>
                </div>}
            </div>,
        });
    };

    const processBatch = () => {
        Modal.confirm({
            title: isApproval ? 'Duyệt các khoản đã chọn?' : 'Xác nhận số liệu các khoản đã chọn?',
            content: `${selectedRowKeys.length} khoản sẽ được kiểm tra lại quyền và trạng thái trước khi xử lý.`,
            width: isMobile ? '100%' : '46%',
            maskClosable: false,
            okText: isApproval ? 'Duyệt hàng loạt' : 'Xác nhận hàng loạt',
            cancelText: 'Hủy',
            onOk: async () => {
                setBatchLoading(true);
                try {
                    const batchResult = await dispatch(processCommissionWorkflowBatch(isApproval, selectedRowKeys));
                    const succeeded = new Set((batchResult.results || []).filter(item => item.success).map(item => item.item_id));
                    setSelectedRowKeys(keys => keys.filter(key => !succeeded.has(key)));
                    setSelectedItems(items => Object.keys(items).reduce((next, key) => {
                        if (!succeeded.has(Number(key))) next[key] = items[key];
                        return next;
                    }, {}));
                    showBatchResult(batchResult);
                    await load(query);
                } catch (error) {
                    // Action đã hiển thị lỗi.
                } finally { setBatchLoading(false); }
            },
        });
    };

    const columns = [
        {
            title: 'Nhân sự', key: 'employee', width: 190,
            render: (_, item) => <CommissionRelatedEmployees employees={item.related_employees?.length
                ? item.related_employees
                : [{
                    employee_id: item.employee?.id,
                    employee_name: item.employee?.full_name,
                    role_key: item.employee?.role_key,
                    role_label: item.employee?.role_label,
                }]} />,
        },
        {
            title: 'Khách hàng / Dịch vụ', key: 'customer_service', width: 300,
            render: (_, item) => <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <CommissionCustomerColumn customer={item.customer} />
                <CommissionServiceColumn customerServiceId={item.customer_service_id} service={item.service} selectedAt={item.service?.selected_at} />
            </Space>,
        },
        {
            title: 'Loại khoản / Trạng thái', key: 'kind_status', width: 180,
            render: (_, item) => {
                const kind = item.commission_kind || {};
                const isAggregate = kind.code === 'AGGREGATE_BONUS';
                const status = isApproval ? item.approval?.status : item.reconciliation?.status;
                const statusView = lifecycleStatus(status);
                return <Space direction="vertical" size={4}>
                    <Tooltip title={kind.description || ''}>
                        <Tag color={isAggregate ? 'purple' : 'blue'}>{kind.label || 'Hoa hồng'}</Tag>
                    </Tooltip>
                    <Tag color={statusView.color}>{statusView.label}</Tag>
                    {isAggregate && kind.source_count > 0 ? <Text type="secondary">Từ {kind.source_count} khoản dịch vụ</Text> : null}
                </Space>;
            },
        },
        {
            title: 'Căn cứ / Kết quả hoa hồng', key: 'commission_amounts', width: 275,
            render: (_, item) => <Space direction="vertical" size={1} style={{ width: '100%' }}>
                <Text type="secondary">Doanh thu tính: <Text strong>{money(item.base_revenue)}</Text></Text>
                {item.rate_value !== null && item.rate_value !== undefined
                    ? <Text type="secondary">Tỷ lệ: <Text strong>{formatCommissionRate(item.rate_value)}</Text></Text>
                    : item.calculation_type === 'CALCULATE_PROGRESSIVE'
                        ? <Text type="secondary">Tỷ lệ: <Text strong>Theo bậc doanh thu</Text></Text>
                        : null}
                <Text>Hoa hồng: <Text strong>{money(item.gross_amount)}</Text></Text>
                {Number(item.hold_amount) > 0 && <Text type="warning">Tạm giữ: {money(item.hold_amount)}</Text>}
                {Number(item.adjustment_amount) !== 0 && <Text type="secondary">Điều chỉnh: {money(item.adjustment_amount)}</Text>}
                <Text>Được ghi nhận: <Text strong>{money(item.net_amount)}</Text></Text>
            </Space>,
        },
        {
            title: 'Thao tác',
            key: 'actions',
            fixed: 'right',
            width: isApproval ? 240 : 220,
            align: 'center',
            render: (_, item) => {
                const canReconcile =
                    allowAction &&
                    !isApproval &&
                    item.reconciliation?.status === 'PENDING' &&
                    item.period_status !== 'LOCKED';

                const canApprove =
                    allowAction &&
                    isApproval &&
                    item.reconciliation?.status === 'RECONCILED' &&
                    item.approval?.status === 'PENDING' &&
                    item.period_status !== 'LOCKED';

                const approvalMenuItems = [
                    {
                        key: 'reject',
                        danger: true,
                        icon: <CloseOutlined />,
                        label: 'Không duyệt',
                        onClick: () =>
                            setRejectItem({
                                item,
                                decision: 'REJECT_FINAL',
                            }),
                    },
                ];

                return (
                    <Space size={6} wrap={false}>
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => setDetailId(item.id)}
                        >
                            Chi tiết
                        </Button>

                        {canReconcile && (
                            <Popconfirm
                                title="Xác nhận số liệu"
                                description="Bạn đã kiểm tra và xác nhận số liệu khoản hoa hồng này là chính xác?"
                                onConfirm={() => reconcile(item)}
                                okText="Xác nhận"
                                cancelText="Hủy"
                            >
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<CheckOutlined />}
                                >
                                    Xác nhận
                                </Button>
                            </Popconfirm>
                        )}

                        {canApprove && (
                            <>
                                <Popconfirm
                                    title="Duyệt khoản hoa hồng"
                                    description="Khoản hoa hồng sẽ được duyệt để chuẩn bị chốt kỳ và chi trả."
                                    onConfirm={() => decide(item, 'APPROVED')}
                                    okText="Duyệt"
                                    cancelText="Hủy"
                                >
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<CheckOutlined />}
                                    >
                                        Duyệt
                                    </Button>
                                </Popconfirm>

                                <Dropdown
                                    menu={{ items: approvalMenuItems }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button
                                        size="small"
                                        icon={<MoreOutlined />}
                                        aria-label="Thao tác khác"
                                    />
                                </Dropdown>
                            </>
                        )}
                    </Space>
                );
            },
        }
    ];

    const filters = [
        <DatePicker
            key="period_key"
            picker="month"
            allowClear={false}
            value={moment(periodKey, 'YYYY-MM')}
            format="MM/YYYY"
            placeholder="Kỳ hoa hồng"
            className="table-button"
            style={{ width: '250px' }}
            onChange={value => changeFilter('period_key', value.format('YYYY-MM'))}
        />,
        <BaseSelect
            key="employee_id"
            options={employeeOptions}
            optionValue="value"
            optionLabel="label"
            showSearch
            placeholder="Nhân viên"
            value={query.employee_id ? Number(query.employee_id) : undefined}
            onChange={value => changeFilter('employee_id', value)}
            style={{ width: '250px' }} className="table-button"
        />,
        <Select
            key="service_group_id"
            allowClear showSearch optionFilterProp="label"
            placeholder="Nhóm dịch vụ"
            value={query.service_group_id ? Number(query.service_group_id) : undefined}
            onChange={value => {
                const nextQuery = cleanObject({ ...query, service_group_id: value, service_id: undefined, page: 1 });
                setSelectedRowKeys([]); setSelectedItems({});
                history.push({ pathname: location.pathname, search: qs.stringify(nextQuery) });
            }}
            style={{ width: '250px' }} className="table-button"
            options={filterOptions.service_groups || []}
        />,
        <Select
            key="service_id"
            allowClear showSearch optionFilterProp="label"
            placeholder="Dịch vụ"
            value={query.service_id ? Number(query.service_id) : undefined}
            onChange={value => changeFilter('service_id', value)}
            style={{ width: '250px' }} className="table-button"
            options={(filterOptions.services || []).filter(item => !query.service_group_id || String(item.service_group_id) === String(query.service_group_id))}
        />,
        <Select
            key="status"
            allowClear
            placeholder="Trạng thái"
            value={query.status || undefined}
            onChange={value => changeFilter('status', value)}
            style={{ width: '250px' }}
            className="table-button"
            options={(isApproval ? [
                'PENDING',
                'APPROVED',
                // 'RETURN_FOR_CORRECTION', // Tạm thời không dùng đến RETURN_FOR_CORRECTION
                'REJECT_FINAL'] : ['PENDING', 'RECONCILED']).map(value => ({ value, label: lifecycleStatus(value).label }))}
        />,
    ];

    return <div>
        {!embedded && <PageTitle routes={[{ path: '/', breadcrumbName: 'Trang chủ' }, { path: '#', breadcrumbName: isApproval ? 'Duyệt hoa hồng' : 'Kiểm tra số liệu hoa hồng' }]} title={isApproval ? 'Duyệt hoa hồng' : 'Kiểm tra số liệu hoa hồng'} />}
        <Alert
            type="info"
            message={isApproval
                ? 'Chỉ các khoản đã được xác nhận ở bước Kiểm tra số liệu mới xuất hiện tại đây. Hãy xem chi tiết và quyết định Duyệt, Yêu cầu chỉnh sửa hoặc Không duyệt; khoản được duyệt sẽ được dùng khi chốt kỳ và chi trả.'
                : 'Các khoản tại đây được hệ thống tính từ dịch vụ, khoản thu, người hưởng và chính sách hoa hồng. Hãy xem chi tiết, đối chiếu doanh thu và số tiền hoa hồng rồi Xác nhận; khoản đã xác nhận sẽ chuyển sang bước Duyệt hoa hồng.'}
            style={{ marginBottom: 16 }}
        />
        <TableActionBar
            isShowAddButton={false}
            isShowDeleteButton={false}
            showSearch={false}
            showFilter={false}
            activeFilter={Boolean(query.period_key || query.status || query.employee_id || query.service_group_id || query.service_id)}
        >
            {filters}
            {allowAction && <Button className="table-button" type="primary" icon={<CheckOutlined />} loading={batchLoading} disabled={!eligibleSelectedCount} onClick={processBatch}>
                {isApproval ? 'Duyệt hàng loạt' : 'Xác nhận hàng loạt'} ({selectedRowKeys.length} đã chọn)
            </Button>}
            <Button className="table-button" icon={<ReloadOutlined />} loading={loading} onClick={() => load(query)}>Làm mới</Button>
        </TableActionBar>
        <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={result.data || []}
            rowSelection={allowAction ? {
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: changeSelection,
            } : undefined}
            scroll={{ x: isApproval ? 1185 : 1165 }}
            pagination={{
                current: result.current_page || 1,
                pageSize: result.per_page || 30,
                total: result.total || 0,
                showTotal: total => `Tổng ${total} khoản`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '50', '100'],
            }}
            onChange={changeTable}
        />
        <CommissionDashboardDetailDrawer itemId={detailId} visible={!!detailId} onClose={() => setDetailId(null)} />
        <Modal visible={!!rejectItem} width={isMobile ? '100%' : '58%'} maskClosable={false} title={rejectItem?.decision === 'REJECT_FINAL' ? 'Không duyệt khoản hoa hồng' : 'Yêu cầu chỉnh sửa khoản hoa hồng'} okText="Xác nhận" okButtonProps={{ danger: rejectItem?.decision === 'REJECT_FINAL' }} cancelText="Hủy" onCancel={() => setRejectItem(null)} onOk={async () => { const values = await rejectForm.validateFields(); decide(rejectItem.item, rejectItem.decision, values.note); }}>
            <Form form={rejectForm} layout="vertical"><Form.Item name="note" label="Lý do" rules={[{ required: true, message: 'Nhập lý do quyết định.' }]}><Input.TextArea rows={4} maxLength={1000} /></Form.Item></Form>
        </Modal>
    </div>;
}
